import { useEffect, useMemo, useState } from 'react'

import { Box, Button, Spinner, Text } from '@factorialco/deprecated-design-system'
import { F0AvatarList, F0Checkbox } from '@factorialco/f0-react'
import { Tabs } from '@factorialco/f0-react/dist/experimental'
import { GetEmployeeSpendingLimitOverridesDocument } from 'generated/resources/graphql'

import DayPicker from 'modules/core/components/DayPicker'
import Modal from 'modules/core/components/Modal'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { dateBackendToUI, dateUIToBackend } from 'modules/core/lib/dates'
import {
  amountToNumber,
  fromCents,
  isValidAmount,
  toCents,
} from 'modules/core/lib/formatCurrencyUtils'
import { buildUrl } from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'
import MoneyWithCurrency from 'modules/core/lib/money'
import app from 'modules/core/store/models/app'

import { useActiveMarkets } from 'modules/benefits/hooks/useActiveMarkets'
import {
  getAvailableCategories,
  getMarketLimits,
  type SupportedMarket,
} from 'modules/benefits/lib/supportedMarkets'
import { useBulkUpsertSpendingLimits } from 'modules/benefits/lib/useBulkUpsertSpendingLimits'
import {
  MEALS_CATEGORY,
  TRANSPORTATION_CATEGORY,
  type TransactionCategory,
} from 'modules/benefits/lib/transactionCategories'

export interface EditEmployeeSpendingLimitsProps {
  isOpen: boolean
  employees: Array<{
    id: string
    name: string
    avatar?: string
  }>
  onClose: () => void
  onSaved?: () => void | Promise<void>
}

interface EmployeeCategoryLimit {
  employeeId: string
  category: TransactionCategory
  currentHasOverride: boolean
  currentAmountCents: number
  currentExpiresAt: string | null
  currentExpirationDate: string | null
}

interface CategoryState {
  category: TransactionCategory
  label: string
  marketLimit: number
  entries: EmployeeCategoryLimit[]
  sharedAmountCents?: number
  sharedExpirationDate?: string | null
  hasAnyCurrentExpiration: boolean
  hasMixedAmounts: boolean
}

interface CategoryFieldState {
  value: string
  dirty: boolean
  expiration: ExpirationFieldState
}

interface ExpirationFieldState {
  hasExpiration: boolean
  date?: string
  dirty: boolean
}

const normalizeToBackendDate = (dateStr: string | null | undefined): string | null => {
  if (!dateStr) return null

  if (dateStr.includes('T')) {
    return dateStr.split('T')[0] ?? null
  }

  return dateStr
}

const toEndOfDayIso = (date: string) => {
  return new Date(`${date}T23:59:59Z`).toISOString()
}

const getSharedValue = <T,>(values: T[]): T | undefined => {
  const [firstValue, ...restValues] = values

  if (firstValue === undefined) return undefined

  return restValues.every((value) => Object.is(value, firstValue)) ? firstValue : undefined
}

const getCategoryLabel = (market: SupportedMarket, category: TransactionCategory) => {
  switch (category) {
    case MEALS_CATEGORY:
      return market === 'pt'
        ? i18n.t('benefits.homepage.static_benefits.pt.meals.title')
        : i18n.t('benefits.homepage.static_benefits.es.meals.title')
    case TRANSPORTATION_CATEGORY:
      return i18n.t('benefits.homepage.static_benefits.es.transport.title')
    default:
      return category
  }
}

const isSupportedMarket = (market: string | null | undefined): market is SupportedMarket => {
  return market === 'es' || market === 'pt'
}

const getInitialCategoryFields = (categories: CategoryState[]) => {
  return categories.reduce<Partial<Record<TransactionCategory, CategoryFieldState>>>(
    (fields, category) => ({
      ...fields,
      [category.category]: {
        value:
          category.sharedAmountCents === undefined
            ? ''
            : String(fromCents(category.sharedAmountCents)),
        dirty: false,
        expiration: getInitialExpirationField([category.sharedExpirationDate]),
      },
    }),
    {}
  )
}

const getInitialExpirationField = (
  expirationDates: Array<string | null | undefined>
): ExpirationFieldState => {
  const sharedExpirationDate = getSharedValue(expirationDates)

  if (sharedExpirationDate === undefined) {
    return {
      hasExpiration: false,
      date: undefined,
      dirty: false,
    }
  }

  return {
    hasExpiration: sharedExpirationDate !== null,
    date: sharedExpirationDate ?? undefined,
    dirty: false,
  }
}

const getCategoryExpirationField = (
  category: CategoryState,
  categoryFields: Partial<Record<TransactionCategory, CategoryFieldState>>
): ExpirationFieldState => {
  const categoryField = categoryFields[category.category]

  if (categoryField?.expiration.dirty) {
    return categoryField.expiration
  }

  return getInitialExpirationField([category.sharedExpirationDate])
}

const getDefaultLimit = (
  marketLimits: Partial<Record<TransactionCategory, number>>,
  category: TransactionCategory
) => {
  return marketLimits[category] ?? 0
}

const getExpirationHelperText = (
  category: CategoryState,
  expirationField: ExpirationFieldState
) => {
  if (
    !expirationField.dirty &&
    category.sharedExpirationDate === undefined &&
    category.hasAnyCurrentExpiration
  ) {
    return i18n.t('benefits.spending_limits.some_limits_have_expiration')
  }

  return i18n.t('benefits.spending_limits.expiration_returns_to_previous_setup')
}

export const EditEmployeeSpendingLimits = ({
  isOpen,
  employees,
  onClose,
  onSaved,
}: EditEmployeeSpendingLimitsProps) => {
  const { access } = useCurrentSession()
  const accessLocale = access?.locale ?? 'en'
  const { upsertLimits, loading: isSaving } = useBulkUpsertSpendingLimits()
  const { activeMarkets, loading: loadingActiveMarkets } = useActiveMarkets()

  const currentMarket = useMemo<SupportedMarket>(() => {
    return isSupportedMarket(activeMarkets[0]) ? activeMarkets[0] : 'es'
  }, [activeMarkets])
  const isResolvingMarket = loadingActiveMarkets && !isSupportedMarket(activeMarkets[0])
  const marketLimits = useMemo(() => getMarketLimits(currentMarket), [currentMarket])
  const availableCategories = useMemo(() => getAvailableCategories(currentMarket), [currentMarket])
  const employeeIds = useMemo(() => employees.map((employee) => employee.id), [employees])
  const expectedOverrideCount = useMemo(() => {
    return employeeIds.length * Math.max(availableCategories.length, 1)
  }, [availableCategories.length, employeeIds.length])
  const overridesPageSize = Math.min(expectedOverrideCount, 1000)

  const { data, loading, refetch } = useQuery(GetEmployeeSpendingLimitOverridesDocument, {
    variables: { employeeIds, activeOnly: true, first: overridesPageSize },
    skip: !isOpen || employees.length === 0 || isResolvingMarket,
    fetchPolicy: 'network-only',
  })
  const hasTruncatedOverrides =
    (data?.benefits?.spendingLimitOverridesConnection?.totalCount ?? 0) > overridesPageSize

  const overridesFromServer = useMemo(() => {
    const nodes = data?.benefits?.spendingLimitOverridesConnection?.nodes ?? []
    const now = new Date()

    return nodes.filter((override) => {
      if (!override.expiresAt) return true
      return new Date(override.expiresAt) > now
    })
  }, [data?.benefits?.spendingLimitOverridesConnection?.nodes])

  const overridesByEmployeeAndCategory = useMemo(() => {
    return overridesFromServer.reduce((employeeMap, override) => {
      const employeeOverrides =
        employeeMap.get(override.employeeId) ?? new Map<TransactionCategory, typeof override>()
      employeeOverrides.set(override.category, override)
      employeeMap.set(override.employeeId, employeeOverrides)
      return employeeMap
    }, new Map<string, Map<TransactionCategory, (typeof overridesFromServer)[number]>>())
  }, [overridesFromServer])

  const categoryStates = useMemo<CategoryState[]>(() => {
    return availableCategories.map((category) => {
      const entries = employees.map<EmployeeCategoryLimit>((employee) => {
        const override = overridesByEmployeeAndCategory.get(employee.id)?.get(category)
        return {
          employeeId: employee.id,
          category,
          currentHasOverride: override != null,
          currentAmountCents: override?.amountCents ?? getDefaultLimit(marketLimits, category),
          currentExpiresAt: override?.expiresAt ?? null,
          currentExpirationDate: normalizeToBackendDate(override?.expiresAt),
        }
      })

      const sharedAmountCents = getSharedValue(entries.map((entry) => entry.currentAmountCents))
      const sharedExpirationDate = getSharedValue(
        entries.map((entry) => entry.currentExpirationDate)
      )

      return {
        category,
        label: getCategoryLabel(currentMarket, category),
        marketLimit: getDefaultLimit(marketLimits, category),
        entries,
        sharedAmountCents,
        sharedExpirationDate,
        hasAnyCurrentExpiration: entries.some((entry) => entry.currentExpirationDate != null),
        hasMixedAmounts: sharedAmountCents === undefined,
      }
    })
  }, [availableCategories, currentMarket, employees, marketLimits, overridesByEmployeeAndCategory])

  const [categoryFields, setCategoryFields] = useState<
    Partial<Record<TransactionCategory, CategoryFieldState>>
  >({})
  const [activeCategory, setActiveCategory] = useState<TransactionCategory | null>(null)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (!isOpen) {
      setIsInitialized(false)
      return
    }

    if (isResolvingMarket) return
    if (loading) return
    if (hasTruncatedOverrides) return
    if (isInitialized) return

    setCategoryFields(getInitialCategoryFields(categoryStates))
    setActiveCategory((currentActiveCategory) => {
      if (
        currentActiveCategory &&
        categoryStates.some(({ category }) => category === currentActiveCategory)
      ) {
        return currentActiveCategory
      }

      return categoryStates[0]?.category ?? null
    })
    setIsInitialized(true)
  }, [categoryStates, hasTruncatedOverrides, isInitialized, isOpen, isResolvingMarket, loading])

  const activeCategoryState = useMemo(() => {
    if (!activeCategory) return categoryStates[0] ?? null

    return (
      categoryStates.find((category) => category.category === activeCategory) ??
      categoryStates[0] ??
      null
    )
  }, [activeCategory, categoryStates])

  const activeCategoryField = activeCategoryState
    ? categoryFields[activeCategoryState.category]
    : undefined
  const activeExpirationField = activeCategoryState
    ? getCategoryExpirationField(activeCategoryState, categoryFields)
    : undefined

  const hasChanges =
    activeCategoryField?.dirty === true || activeCategoryField?.expiration.dirty === true
  const hasHiddenDrafts = categoryStates.some((category) => {
    if (category.category === activeCategoryState?.category) return false

    const categoryField = categoryFields[category.category]
    return categoryField?.dirty === true || categoryField?.expiration.dirty === true
  })

  const handleClose = () => {
    setIsInitialized(false)
    onClose()
  }

  const handleCategoryChange = (category: TransactionCategory, value: string) => {
    setCategoryFields((currentFields) => ({
      ...currentFields,
      [category]: {
        value,
        dirty: true,
        expiration: currentFields[category]?.expiration ?? {
          hasExpiration: false,
          date: undefined,
          dirty: false,
        },
      },
    }))
  }

  const updateCategoryExpiration = (
    category: TransactionCategory,
    expiration: ExpirationFieldState
  ) => {
    setCategoryFields((currentFields) => {
      const currentField = currentFields[category]

      if (!currentField) return currentFields

      return {
        ...currentFields,
        [category]: {
          ...currentField,
          expiration,
        },
      }
    })
  }

  const handleCategoryExpirationDateChange = (category: TransactionCategory, value: string) => {
    updateCategoryExpiration(category, {
      hasExpiration: true,
      date: dateUIToBackend(value),
      dirty: true,
    })
  }

  const handleCategoryExpirationToggle = (
    category: TransactionCategory,
    checked: boolean | 'indeterminate'
  ) => {
    const categoryState = categoryStates.find((entry) => entry.category === category)

    if (!categoryState || checked === 'indeterminate') return

    const currentExpirationField = getCategoryExpirationField(categoryState, categoryFields)

    updateCategoryExpiration(category, {
      hasExpiration: checked,
      date: currentExpirationField.date,
      dirty: true,
    })
  }

  const handleSave = async () => {
    if (!activeCategoryState || !activeCategoryField) {
      return
    }

    if (hasTruncatedOverrides) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('benefits.spending_limits.too_many_selected_employees'),
      })
      return
    }

    if (!hasChanges) {
      return
    }

    if (activeCategoryField.expiration.hasExpiration && !activeCategoryField.expiration.date) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('benefits.spending_limits.invalid_expiration_date'),
      })
      return
    }

    let nextAmountCents: number | undefined
    if (activeCategoryField.dirty) {
      const rawValue = activeCategoryField.value ?? ''
      if (!isValidAmount(rawValue)) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'benefits.tabs.employees.activation_results.modal.edit_spending_limit.invalid_amount'
          ),
        })
        return
      }

      const amount = amountToNumber(rawValue)

      if (amount < 0) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'benefits.tabs.employees.activation_results.modal.edit_spending_limit.invalid_amount'
          ),
        })
        return
      }

      nextAmountCents = toCents(amount)
      if (nextAmountCents > activeCategoryState.marketLimit) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'benefits.tabs.employees.activation_results.modal.edit_spending_limit.surpassed_default_limit'
          ),
        })
        return
      }
    }

    const expirationField = activeCategoryField.expiration
    const categoryWasEdited = activeCategoryField.dirty === true

    const overrides = activeCategoryState.entries.flatMap((entry) => {
      if (!categoryWasEdited && !expirationField.dirty && !entry.currentHasOverride) {
        return []
      }

      const nextExpirationDate = expirationField.dirty
        ? expirationField.hasExpiration
          ? (expirationField.date ?? null)
          : null
        : entry.currentExpirationDate
      const resolvedAmountCents = categoryWasEdited ? nextAmountCents : entry.currentAmountCents

      if (resolvedAmountCents === undefined) return []

      if (
        entry.currentAmountCents === resolvedAmountCents &&
        entry.currentExpirationDate === nextExpirationDate
      ) {
        return []
      }

      return {
        employeeId: entry.employeeId,
        category: entry.category,
        amountCents: resolvedAmountCents,
        expiresAt: expirationField.dirty
          ? expirationField.hasExpiration && expirationField.date
            ? toEndOfDayIso(expirationField.date)
            : null
          : entry.currentExpiresAt,
      }
    })

    if (overrides.length === 0) {
      if (!hasHiddenDrafts) {
        handleClose()
      }
      return
    }

    await upsertLimits(overrides, async () => {
      await Promise.all([refetch(), onSaved?.()])
      handleClose()
    })
  }

  if (!isOpen || employees.length === 0) return null

  return (
    <Modal
      title={i18n.t(
        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.step_title'
      )}
      description={i18n.t('benefits.spending_limits.limit_applies_to_selected_employees')}
      onClose={handleClose}
      footer={
        <>
          <Button type="secondary" onClick={handleClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleSave}
            disabled={!hasChanges || hasTruncatedOverrides || isSaving}
          >
            {i18n.t(
              'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_modal_button'
            )}
          </Button>
        </>
      }
    >
      {isResolvingMarket || loading || (!isInitialized && !hasTruncatedOverrides) ? (
        <Box
          width="full"
          alignItems="center"
          justifyContent="center"
          style={{ minHeight: '200px' }}
        >
          <Spinner size="s32" />
        </Box>
      ) : hasTruncatedOverrides ? (
        <Text.Normal color="grey800">
          {i18n.t('benefits.spending_limits.too_many_selected_employees')}
        </Text.Normal>
      ) : (
        <Box flexDirection="column" gap="s12">
          <F0AvatarList
            avatars={employees.map((employee) => {
              const [firstName = '', ...restNames] = employee.name.split(' ')

              return {
                firstName,
                lastName: restNames.join(' '),
                src: employee.avatar ? buildUrl(employee.avatar) : undefined,
                type: 'person' as const,
              }
            })}
            size="md"
            type="person"
          />

          <Box className="[&_nav[aria-label='secondary-navigation']>div:last-child>ul]:!px-0">
            <Tabs
              activeTabId={activeCategoryState?.category ?? ''}
              setActiveTabId={(tabId: string | undefined) => {
                const nextCategory = categoryStates.find(
                  (category) => category.category === tabId
                )?.category

                if (nextCategory) {
                  setActiveCategory(nextCategory)
                }
              }}
              tabs={categoryStates.map((category) => ({
                id: category.category,
                label: category.label,
              }))}
            />
          </Box>

          {activeCategoryState && activeCategoryField && activeExpirationField && (
            <Box flexDirection="column" gap="s12" paddingTop="s4">
              <TextField
                missingLabel={i18n.t('benefits.spending_limits.amount')}
                placeholder={
                  activeCategoryState.hasMixedAmounts
                    ? i18n.t(
                        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.mixed_values_placeholder'
                      )
                    : i18n.t(
                        'benefits.tabs.employees.activation_results.modal.edit_spending_limit.edit_limits_modal_placeholder'
                      )
                }
                value={activeCategoryField.value}
                onChange={(value) => handleCategoryChange(activeCategoryState.category, value)}
                type="number"
              />

              <Text.Normal color="grey600" size="200">
                {i18n.t('benefits.spending_limits.legal_limit', {
                  amount: MoneyWithCurrency({
                    amount: activeCategoryState.marketLimit,
                    currency: 'EUR',
                    locale: accessLocale,
                  }),
                })}
              </Text.Normal>

              <Box flexDirection="column" gap="s4" paddingTop="s4">
                <F0Checkbox
                  title={i18n.t('benefits.spending_limits.set_expiration')}
                  checked={activeExpirationField.hasExpiration}
                  onCheckedChange={(checked) =>
                    handleCategoryExpirationToggle(activeCategoryState.category, checked)
                  }
                />
                <Text.Normal color="grey600" size="200">
                  {getExpirationHelperText(activeCategoryState, activeExpirationField)}
                </Text.Normal>
              </Box>

              {activeExpirationField.hasExpiration && (
                <DayPicker
                  label={i18n.t('benefits.spending_limits.expiration_date')}
                  value={
                    activeExpirationField.date
                      ? dateBackendToUI(activeExpirationField.date)
                      : undefined
                  }
                  onChange={(value) =>
                    handleCategoryExpirationDateChange(activeCategoryState.category, value)
                  }
                  minDate={new Date()}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </Modal>
  )
}
